import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { useRankingSource } from '@/composables/use-ranking-devtools'
import { DEFAULT_CATEGORY_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { useAppStore } from '@/store/app-store'
import {
    type CategoryType,
    RedirectMechanism,
    RedirectReason,
    type ShortGameType,
    type ShortGameZipType,
} from '@/types'
import { unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'

type Category = {
    id: string
    categoryType: CategoryType
    total: number
    totalPages: number
}

type UseCategoryPageDataParams = {
    categoryName: Ref<string>
    pageType: Ref<CategoryType>
    page: Ref<number>
    pinnedGame: Ref<string | undefined>
}

export const useCategoryPageData = ({ categoryName, pageType, page, pinnedGame }: UseCategoryPageDataParams) => {
    const appStore = useAppStore()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getGamesByCategories, getGamesCategoriesSeoById } = useAPI()

    // Query 1: Category SEO Info
    const seoQueryKey = computed(() => ['category-seo-info', categoryName.value, pageType.value, currentLocale.value])
    const seoQueryPersister = usePersisterFn(Persisters.MediumCardinality)

    const {
        data: seoData,
        suspense: seoSuspense,
        isError: seoHasError,
    } = useQuery({
        queryKey: seoQueryKey,
        queryFn: async () => {
            const result = await getGamesCategoriesSeoById(categoryName.value, currentLocale.value, pageType.value)

            if (result.status === 'error') {
                throw new Error(result.message)
            }

            if (result.status === 'redirect') {
                return {
                    categoryRedirect: result.redirect,
                }
            }

            if (result.status !== 'ok') {
                return { fetchStatus: result.httpStatus }
            }

            if (result.data.categoryType !== pageType.value) {
                return {
                    categoryRedirect: {
                        redirectTarget: {
                            type: 'category',
                            cat_type: result.data.categoryType,
                            cat_hru: categoryName.value,
                            reason: RedirectReason.CATEGORY_TYPE_MISMATCH,
                            mechanism: RedirectMechanism.FRONTEND_REDIRECT,
                        },
                        status: 301,
                    } as const,
                }
            }

            const getSubCategories = (type: 'pathCategories' | 'subCategories') =>
                result.data[type]?.map((subCategory) => ({
                    ...subCategory,
                    href: getCategoryRoute({ hru: subCategory.hru, type: subCategory.categoryType }),
                })) || []

            return {
                categoryInfo: {
                    ...result.data,
                    pathCategories: getSubCategories('pathCategories'),
                    subCategories: getSubCategories('subCategories'),
                    fullTitle: result.data.description || result.data.title,
                },
            }
        },
        persister: seoQueryPersister,
    })

    // Query 2: Category Games Data
    const gamesQueryKey = computed(() => [
        'category-games-data',
        categoryName.value,
        pageType.value,
        page.value,
        pinnedGame.value,
        currentLocale.value,
        appStore.platform,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.withDebugInfo,
        appStore.contentScope,
    ])
    const firstPagePersister = usePersisterFn(Persisters.MediumCardinality)
    const gamesQueryPersister = computed(() => {
        if (page.value === 1) {
            return firstPagePersister
        }
        return undefined
    })

    const {
        data: gamesData,
        suspense: gamesSuspense,
        isError: gamesHasError,
    } = useQuery({
        queryKey: gamesQueryKey,
        queryFn: async () => {
            const result: {
                games: ShortGameZipType[]
                category?: Category
                fetchStatus?: 404 | 410
            } = {
                games: [],
            }

            // contentScope indicates we want to show specific content to user
            // meanwhile games on unsupported platforms need to be shown only for SEO
            // so defined contentScope disables this flag
            const includeUnsupportedPlatforms = !appStore.contentScope

            if (isNaN(page.value)) {
                result.fetchStatus = 404
                return result
            }

            const response = await getGamesByCategories({
                categoriesIds: [categoryName.value],
                pagination: {
                    page: page.value,
                    pageSize: DEFAULT_CATEGORY_PAGE_SIZE,
                },
                platform: appStore.platform,
                locale: currentLocale.value,
                withDebugInfo: appStore.withDebugInfo,
                pageType: pageType.value,
                pinnedGame: pinnedGame.value,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                includeUnsupportedPlatforms,
                scope: appStore.contentScope,
            })

            if (response.status === 'error') {
                throw new Error(response.message)
            }

            if (response.status === 'not-found') {
                result.fetchStatus = response.httpStatus
                return result
            }

            const [fetchedCategory] = response.data.categories

            if (!fetchedCategory) {
                return result
            }

            if (page.value > 1 && fetchedCategory.games.length === 0) {
                result.fetchStatus = 404
                return result
            }

            result.games = fetchedCategory.games.map(zipAPIGame)
            result.category = {
                id: fetchedCategory.id,
                categoryType: fetchedCategory.categoryType,
                total: fetchedCategory.totalCount,
                totalPages: Math.ceil(fetchedCategory.totalCount / DEFAULT_CATEGORY_PAGE_SIZE),
            }

            return result
        },
        persister: gamesQueryPersister,
    })

    // Combine the results
    const games = computed<ShortGameType[]>(() => gamesData.value?.games.map(unzipShortGame) || [])

    const pageSize = computed(() => DEFAULT_CATEGORY_PAGE_SIZE)
    const category = computed(() => gamesData.value?.category)
    const categoryInfo = computed(() => seoData.value?.categoryInfo)

    const categoryRedirect = computed(() => seoData.value?.categoryRedirect)
    const fetchStatus = computed(() => seoData.value?.fetchStatus || gamesData.value?.fetchStatus)
    const hasError = computed(() => seoHasError.value || gamesHasError.value)

    // Combined suspense for SSR - both queries must complete
    const preloadCategoryPageData = async () => {
        await Promise.all([seoSuspense(), gamesSuspense()])
    }

    useRankingSource(() => [{ title: categoryInfo.value?.title || categoryName.value, games: games.value }])

    return {
        games,
        pageSize,
        category,
        categoryInfo,
        categoryRedirect,
        fetchStatus,
        hasError,
        preloadCategoryPageData,
    }
}
